import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Checkbox } from '@/components/ui/Checkbox';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

/**
 * VariationsStep Component
 *
 * Fourth step of onboarding wizard - content variations setup
 * Allows users to configure multi-language and platform-specific variations
 */

type VariationType = 'none' | 'languages' | 'platforms' | 'both';

interface VariationTypeOptionProps {
  label: string;
  description: string;
  materialIcon: string;
  selected: boolean;
  onClick: () => void;
}

function VariationTypeOption({
  label,
  description,
  materialIcon,
  selected,
  onClick,
}: VariationTypeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full p-6 rounded-xl border-2 transition-all duration-200',
        'text-left hover:border-primary-400 hover:shadow-md',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        selected
          ? 'border-primary-500 bg-primary-50 shadow-md focus-visible:ring-0'
          : 'border-gray-200 bg-white'
      )}
      aria-pressed={selected}
    >
      <div className="flex items-start gap-4">
        {/* Icon - using Material Symbols for visual consistency */}
        <div
          className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-colors',
            selected ? 'bg-primary-500 text-primary-contrast' : 'bg-gray-100 text-gray-600'
          )}
        >
          <span className="material-symbols-outlined text-[28px]" aria-hidden="true">
            {materialIcon}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              'text-lg font-semibold mb-1 transition-colors',
              selected ? 'text-primary-900' : 'text-gray-900'
            )}
          >
            {label}
          </h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>

        {/* Checkmark */}
        <div className="flex-shrink-0">
          {selected && (
            <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center animate-fade-in">
              <Icon name={ICONS.CHECK} size="sm" color="text-white" ariaHidden />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

interface LanguageButtonProps {
  code: string;
  label: string;
  selected: boolean;
  onClick: () => void;
}

function LanguageButton({ code, label, selected, onClick }: LanguageButtonProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 rounded-lg border-2 transition-all duration-200',
        'font-medium text-sm',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        'hover:scale-105',
        selected
          ? 'border-primary-500 bg-primary-500 text-primary-contrast'
          : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300'
      )}
      aria-pressed={selected}
      aria-label={selected
        ? t('aria_deselect', { defaultValue: 'Deselect {{label}}', label })
        : t('aria_select', { defaultValue: 'Select {{label}}', label })}
    >
      {code}
    </button>
  );
}

interface VariationsStepProps {
  variationType: VariationType;
  selectedLanguages: string[];
  autoTranslateEnabled: boolean;
  onVariationTypeChange: (type: VariationType) => void;
  onLanguagesChange: (languages: string[]) => void;
  onAutoTranslateChange: (enabled: boolean) => void;
}

const LANGUAGE_KEYS = [
  { code: 'EN', labelKey: 'language_en' },
  { code: 'ES', labelKey: 'language_es' },
  { code: 'DE', labelKey: 'language_de' },
  { code: 'FR', labelKey: 'language_fr' },
  { code: 'IT', labelKey: 'language_it' },
  { code: 'PT', labelKey: 'language_pt' },
  { code: 'NL', labelKey: 'language_nl' },
  { code: 'JA', labelKey: 'language_ja' },
  { code: 'ZH', labelKey: 'language_zh' },
  { code: 'PL', labelKey: 'language_pl' },
];

export function VariationsStep({
  variationType,
  selectedLanguages,
  autoTranslateEnabled,
  onVariationTypeChange,
  onLanguagesChange,
  onAutoTranslateChange,
}: VariationsStepProps) {
  const { t } = useTranslation();
  const autoTranslateLock = useFeatureLock('auto_translate');

  const variationTypes: Array<{
    type: VariationType;
    label: string;
    description: string;
    materialIcon: string;
  }> = [
    {
      type: 'none',
      label: t('onboarding_variations_type_none_label', { defaultValue: 'No, I keep it simple' }),
      description: t('onboarding_variations_type_none_desc', { defaultValue: 'I post the same content everywhere' }),
      materialIcon: 'check_circle',
    },
    {
      type: 'languages',
      label: t('onboarding_variations_type_languages_label', { defaultValue: 'Yes, I post in multiple languages' }),
      description: t('onboarding_variations_type_languages_desc', { defaultValue: 'Translate content for different audiences' }),
      materialIcon: 'translate',
    },
    {
      type: 'platforms',
      label: t('onboarding_variations_type_platforms_label', { defaultValue: 'Yes, I adapt content for different platforms' }),
      description: t('onboarding_variations_type_platforms_desc', { defaultValue: 'Customize content for each social network' }),
      materialIcon: 'devices',
    },
    {
      type: 'both',
      label: t('onboarding_variations_type_both_label', { defaultValue: 'Yes, both' }),
      description: t('onboarding_variations_type_both_desc', { defaultValue: 'Languages and platform-specific variations' }),
      materialIcon: 'settings',
    },
  ];

  const handleLanguageToggle = (code: string) => {
    if (selectedLanguages.includes(code)) {
      onLanguagesChange(selectedLanguages.filter((lang) => lang !== code));
    } else {
      onLanguagesChange([...selectedLanguages, code]);
    }
  };

  const showLanguageSelector = variationType === 'languages' || variationType === 'both';

  return (
    <div className="text-center">
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        {t('onboarding_variations_title')}
      </h2>
      <p className="text-gray-600 mb-8">
        {t('onboarding_variations_description')}
      </p>

      {/* Variation Type Options */}
      <div className="space-y-3 mb-6">
        {variationTypes.map(({ type, label, description, materialIcon }) => (
          <VariationTypeOption
            key={type}
            label={label}
            description={description}
            materialIcon={materialIcon}
            selected={variationType === type}
            onClick={() => onVariationTypeChange(type)}
          />
        ))}
      </div>

      {/* Language Selector (shown when languages or both selected) */}
      {showLanguageSelector && (
        <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded-xl animate-fade-in">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-left">
            {t('onboarding_variations_select_languages')}
          </h3>

          {/* Language Buttons */}
          <div className="flex flex-wrap gap-2 mb-5">
            {LANGUAGE_KEYS.map((language) => (
              <LanguageButton
                key={language.code}
                code={language.code}
                label={t(language.labelKey, { defaultValue: language.code })}
                selected={selectedLanguages.includes(language.code)}
                onClick={() => handleLanguageToggle(language.code)}
              />
            ))}
          </div>

          {/* Auto-Translate Checkbox */}
          <div className="pt-4 border-t border-gray-200">
            <label
              htmlFor="auto-translate-checkbox"
              className="flex items-start gap-3 cursor-pointer text-left"
            >
              <Checkbox
                id="auto-translate-checkbox"
                checked={autoTranslateEnabled && !autoTranslateLock.locked}
                onCheckedChange={onAutoTranslateChange}
                disabled={autoTranslateLock.locked}
                inline
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">
                  {t('onboarding_variations_auto_translate')}
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {t('onboarding_variations_auto_translate_desc')}
                </p>
                {autoTranslateLock.locked && (
                  <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-700">
                    <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                    {autoTranslateLock.label}
                  </span>
                )}
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Helper Text */}
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name={ICONS.BULB} size="md" color="text-gray-600" ariaHidden />
          <p className="text-sm text-gray-700 text-left">
            {t('onboarding_variations_hint')}
          </p>
        </div>
      </div>
    </div>
  );
}
